import React from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Input,
  Spinner,
  Text,
  Textarea,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { FiCreditCard, FiLayers } from "react-icons/fi";
import {
  PersianDatePicker,
  PersianDayValue,
} from "../common/PersianDatePicker";
import {
  getTodayPersianDay,
  toPersianDayValue,
  toGregorianIsoDate,
} from "../../utils/persianDate";
import { useFunds } from "../../hooks/useFunds";
import { FundSelectionCard } from "../funds/FundSelectionCard";

interface PaymentBatchCardFormProps {
  mode?: "single" | "batch";
  initialData?: any;
  includeClubRewardFields?: boolean;
  showTitleDescriptionFields?: boolean;
  onSubmit: (data: any) => void;
  onCancel: () => void;
}

export const PaymentBatchCardForm: React.FC<PaymentBatchCardFormProps> = ({
  mode = "batch",
  initialData,
  includeClubRewardFields = false,
  showTitleDescriptionFields = true,
  onSubmit,
  onCancel,
}) => {
  const batchData = initialData?.paymentBatch || initialData || {};
  const [title, setTitle] = React.useState(batchData.title || "");
  const [description, setDescription] = React.useState(batchData.description || "");
  const [createMode, setCreateMode] = React.useState<"single" | "batch">(
    batchData.createMode || mode,
  );
  const [quantity, setQuantity] = React.useState(
    batchData.quantity || (mode === "single" ? 1 : 10),
  );
  const [amount, setAmount] = React.useState(
    batchData.amount ? String(batchData.amount) : "",
  );
  const [expiresAt, setExpiresAt] = React.useState<PersianDayValue>(
    toPersianDayValue(batchData.expiresAt),
  );
  const [fundSeoRegisterNo, setFundSeoRegisterNo] = React.useState(
    batchData.fundSeoRegisterNo || "",
  );
  const [pointsCost, setPointsCost] = React.useState(
    initialData?.pointsCost ? String(initialData.pointsCost) : "",
  );
  const [status, setStatus] = React.useState(initialData?.status || "active");
  const today = React.useMemo(() => getTodayPersianDay(), []);
  const { data: fundsData, isLoading: isFundsLoading } = useFunds();
  const funds = React.useMemo(() => fundsData?.items || [], [fundsData?.items]);
  const selectedFund = React.useMemo(
    () =>
      funds.find(
        (fund) =>
          String(fund.nav?.seoRegisterNo || fund.fundRegisterCode || "") ===
          fundSeoRegisterNo,
      ),
    [fundSeoRegisterNo, funds],
  );

  React.useEffect(() => {
    if (fundSeoRegisterNo || funds.length === 0) {
      return;
    }

    const firstFundKey = String(
      funds[0].nav?.seoRegisterNo || funds[0].fundRegisterCode || "",
    );

    if (firstFundKey) {
      setFundSeoRegisterNo(firstFundKey);
    }
  }, [fundSeoRegisterNo, funds]);

  const inputBg = useColorModeValue("white", "#23272F");
  const inputBorder = useColorModeValue("#e5e7eb", "#444");
  const labelColor = useColorModeValue("gray.700", "gray.200");
  const typeActiveBg = useColorModeValue("brand.500", "brand.400");
  const typeActiveText = "white";
  const typeInactiveText = useColorModeValue("gray.600", "gray.300");
  const typeActiveBorder = useColorModeValue("brand.500", "brand.400");
  const modeHoverBg = useColorModeValue("gray.100", "gray.700");
  const amountDigits = amount.replace(/[^\d]/g, "");
  const formattedAmount = amountDigits
    ? Number(amountDigits).toLocaleString("en-US")
    : "";

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const payload = {
      title,
      description,
      createMode,
      quantity: createMode === "single" ? 1 : Number(quantity),
      amount: Number(String(amount).replace(/[^\d]/g, "")),
      fundSeoRegisterNo,
      fundName: selectedFund?.fundName?.trim() || "",
      expiresAt: toGregorianIsoDate(expiresAt),
    } as any;

    if (includeClubRewardFields) {
      payload.pointsCost = Number(pointsCost || 0);
      payload.status = status;
    }

    onSubmit(payload);
  };

  return (
    <form onSubmit={handleSubmit}>
      <VStack spacing={5} align="stretch" w="100%" py="10px">
        {mode === "batch" && (
          <FormControl>
            <FormLabel fontWeight="bold" color={labelColor}>
              نوع ایجاد
            </FormLabel>
            <HStack spacing={3} align="stretch">
              {(
                [
                  {
                    value: "batch" as const,
                    label: "بسته کارت",
                    icon: FiLayers,
                  },
                  {
                    value: "single" as const,
                    label: "کارت تکی",
                    icon: FiCreditCard,
                  },
                ] as const
              ).map(({ value, label, icon }) => {
                const active = createMode === value;

                return (
                  <Box
                    key={value}
                    flex={1}
                    onClick={() => {
                      setCreateMode(value);
                      if (value === "single") {
                        setQuantity(1);
                      }
                    }}
                    cursor="pointer"
                    borderRadius="lg"
                    px={3}
                    py={3}
                    bg={active ? typeActiveBg : "transparent"}
                    border="1.5px solid"
                    borderColor={active ? typeActiveBorder : inputBorder}
                    transition="all 0.2s"
                    _hover={!active ? { bg: modeHoverBg } : {}}
                  >
                    <HStack justify="center" spacing={2}>
                      <Icon
                        verticalAlign="middle"
                        as={icon as any}
                        boxSize={4}
                        color={active ? typeActiveText : typeInactiveText}
                      />
                      <Text
                        fontSize="sm"
                        fontWeight="semibold"
                        color={active ? typeActiveText : typeInactiveText}
                      >
                        {label}
                      </Text>
                    </HStack>
                  </Box>
                );
              })}
            </HStack>
          </FormControl>
        )}

        <FormControl isRequired>
          <FormLabel fontWeight="bold" color={labelColor}>
            صندوق
          </FormLabel>
          {isFundsLoading ? (
            <HStack pt={2} color="gray.500">
              <Spinner size="sm" />
              <Text fontSize="sm">در حال بارگذاری فهرست صندوق‌ها</Text>
            </HStack>
          ) : (
            <FundSelectionCard
              fundData={funds}
              value={fundSeoRegisterNo}
              onChange={setFundSeoRegisterNo}
              minHeight="96px"
              logoSize="72px"
            />
          )}
        </FormControl>

        {showTitleDescriptionFields && (
          <>
            <FormControl isRequired>
              <FormLabel fontWeight="bold" color={labelColor}>
                عنوان
              </FormLabel>
              <Input
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="عنوان بسته کارت را وارد کنید"
                borderRadius="lg"
                bg={inputBg}
                borderColor={inputBorder}
                textAlign="center"
              />
            </FormControl>

            <FormControl>
              <FormLabel fontWeight="bold" color={labelColor}>
                توضیحات
              </FormLabel>
              <Textarea
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="توضیحات موردنظر را وارد کنید"
                borderRadius="lg"
                bg={inputBg}
                borderColor={inputBorder}
                rows={4}
                textAlign="center"
              />
            </FormControl>
          </>
        )}

        {createMode === "batch" && (
          <FormControl isRequired>
            <FormLabel fontWeight="bold" color={labelColor}>
              تعداد کارت
            </FormLabel>
            <Input
              type="number"
              min={1}
              value={quantity}
              onChange={(event) => setQuantity(Number(event.target.value || 1))}
              placeholder="تعداد کارت را وارد کنید"
              borderRadius="lg"
              bg={inputBg}
              borderColor={inputBorder}
              textAlign="center"
            />
          </FormControl>
        )}

        <FormControl isRequired>
          <FormLabel fontWeight="bold" color={labelColor}>
            مبلغ هر کارت (ریال)
          </FormLabel>
          <Input
            inputMode="numeric"
            value={formattedAmount}
            onChange={(event) => setAmount(event.target.value)}
            placeholder="مبلغ هر کارت را به ریال وارد کنید"
            borderRadius="lg"
            bg={inputBg}
            borderColor={inputBorder}
            textAlign="center"
          />
        </FormControl>

        {includeClubRewardFields && (
          <>
            <FormControl isRequired>
              <FormLabel fontWeight="bold" color={labelColor}>
                هزینه امتیاز
              </FormLabel>
              <Input
                type="number"
                min={1}
                value={pointsCost}
                onChange={(event) => setPointsCost(event.target.value)}
                borderRadius="lg"
                bg={inputBg}
                borderColor={inputBorder}
                dir="ltr"
                textAlign="center"
              />
            </FormControl>
          </>
        )}

        <FormControl isRequired>
          <FormLabel fontWeight="bold" color={labelColor}>
            تاریخ انقضا
          </FormLabel>
          <PersianDatePicker
            value={expiresAt}
            onChange={setExpiresAt}
            minimumDate={today}
            placeholder="تاریخ انقضا را انتخاب کنید"
          />
        </FormControl>

        {includeClubRewardFields && (
          <FormControl isRequired>
            <FormLabel fontWeight="bold" color={labelColor}>
              وضعیت
            </FormLabel>
            <HStack spacing={3}>
              {(
                [
                  { value: "active", label: "فعال" },
                  { value: "inactive", label: "غیرفعال" },
                ] as const
              ).map((item) => {
                const active = status === item.value;
                return (
                  <Box
                    key={item.value}
                    flex={1}
                    onClick={() => setStatus(item.value)}
                    cursor="pointer"
                    borderRadius="lg"
                    px={3}
                    py={3}
                    bg={active ? typeActiveBg : "transparent"}
                    border="1.5px solid"
                    borderColor={active ? typeActiveBorder : inputBorder}
                    transition="all 0.2s"
                    _hover={!active ? { bg: modeHoverBg } : {}}
                  >
                    <Text
                      textAlign="center"
                      fontSize="sm"
                      fontWeight="semibold"
                      color={active ? typeActiveText : typeInactiveText}
                    >
                      {item.label}
                    </Text>
                  </Box>
                );
              })}
            </HStack>
          </FormControl>
        )}

        <Button type="submit" size="lg" w="100%" mt={2} py={3}>
          ثبت
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          انصراف
        </Button>
      </VStack>
    </form>
  );
};
